import type { ExistingVehicle } from 'models/Vehicles/VehicleModel'
import { VehicleServiceStatus } from 'models/Vehicles/VehicleStateModel'
import { SortDirection } from '../Table/Table'

// Also shown in the column header's tooltip ("Sort by ...")
export enum VehiclesSortKey {
    Number = 'vehicle number',
    Profile = 'profile',
    Status = 'status',
}

export interface VehiclesSort {
    sortKey: VehiclesSortKey
    sortDirection: SortDirection
}

// Clicking the sorted column reverses the order, clicking another column sorts by it in ascending order
export const getNextVehiclesSort = (currentSort: VehiclesSort | null, clickedKey: VehiclesSortKey): VehiclesSort => ({
    sortKey: clickedKey,
    sortDirection: currentSort?.sortKey === clickedKey && currentSort.sortDirection === SortDirection.Asc
        ? SortDirection.Desc
        : SortDirection.Asc,
})

const STATUS_ORDER = [VehicleServiceStatus.InService, VehicleServiceStatus.Parked, VehicleServiceStatus.OutOfService]

interface VehicleSortValues {
    getProfileTitle: (vehicle: ExistingVehicle) => string | undefined
    getStatus: (vehicle: ExistingVehicle) => VehicleServiceStatus | undefined
}

const getSortValue = (vehicle: ExistingVehicle, sortKey: VehiclesSortKey, { getProfileTitle, getStatus }: VehicleSortValues) => {
    switch (sortKey) {
        case VehiclesSortKey.Number:
            return vehicle.id
        case VehiclesSortKey.Profile:
            return getProfileTitle(vehicle)
        case VehiclesSortKey.Status: {
            const status = getStatus(vehicle)
            return status !== undefined ? STATUS_ORDER.indexOf(status) : undefined
        }
    }
}

const compareValues = (a: string | number, b: string | number) =>
    typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })

// Vehicles without a value (no profile, unknown status) are always last
export const sortVehicles = (
    vehicles: ReadonlyArray<ExistingVehicle>,
    { sortKey, sortDirection }: VehiclesSort,
    sortValues: VehicleSortValues,
): ExistingVehicle[] => {
    const directionFactor = sortDirection === SortDirection.Asc ? 1 : -1
    return [...vehicles].sort((vehicleA, vehicleB) => {
        const a = getSortValue(vehicleA, sortKey, sortValues)
        const b = getSortValue(vehicleB, sortKey, sortValues)
        if (a === undefined || b === undefined) {
            return a === b ? 0 : a === undefined ? 1 : -1
        }
        return directionFactor * compareValues(a, b)
    })
}
